import LoadingSpinner from "components/loading/LoadingSpinner";
import { InfoTooltip } from "components/InfoTooltip/InfoTooltip";

import { useSearch } from "hooks/search/useSearch";
import { SearchType } from "hooks/search/types";
import { useMemo } from "react";

import { useFormContext, useWatch } from "react-hook-form";
import { SearchInput } from "components/input/SearchInput";
import { useFilters } from "hooks/search/useFilters";
import { useTranslation } from "react-i18next";
import { CheckboxSection } from "./CheckboxSection";
import { MAX_SELECT_ALL_ITEMS } from "./constants";

export const MultiSelectFilter = ({
  name,
  searchQuery = "",
  searchType = SearchType.QUICK,
}: {
  name: string;
  searchQuery?: string;
  searchType?: SearchType;
}) => {
  const { register, setValue } = useFormContext();
  const searchInput = useWatch({ name: `filters.${name}.search` });
  const activeFilters = useWatch({ name: "filters" });
  const { filters } = useFilters();
  const { t } = useTranslation(["books"]);
  const watchedValues = useWatch({ name: `filters.${name}.values` });
  const selectedFormItems: string[] = useMemo(
    () => watchedValues ?? [],
    [watchedValues],
  );
  const { data: searchData, isFetching: isLoadingSearch } = useSearch(
    {
      query: searchQuery,
      type: searchType,
      facets: [name],
      facetSearch: searchInput ? { field: name, text: searchInput } : undefined,
      facetSize: 5000,
      pageSize: 0,
      filters: activeFilters,
    },
    { debounce: 250 },
  );

  const items = useMemo(() => {
    const counts =
      searchData?.facets?.find((f) => f.field === name)?.counts ?? [];
    return counts
      .map((item) => item.value)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [searchData, name]);

  const filterConfig = filters.find((filter) => filter.name === name);

  const suggestions = useMemo(
    () => items.filter((item) => !selectedFormItems.includes(item)),
    [items, selectedFormItems],
  );

  const allSelected = useMemo(
    () =>
      items.length > 0 &&
      items.every((item) => selectedFormItems?.includes(item)),
    [items, selectedFormItems],
  );

  const showSelectAll = filterConfig?.showSelectAll;
  const selectAllDisabled = items.length > MAX_SELECT_ALL_ITEMS;

  const handleSelectAll = () => {
    setValue(`filters.${name}.values`, [...items]);
  };

  const handleDeselectAll = () => {
    setValue(`filters.${name}.values`, []);
  };

  const hasSelection = selectedFormItems.length > 0;

  return (
    <div className="flex flex-col gap-y-2">
      <SearchInput
        className="mx-6 mb-2 w-[calc(100%-3rem)]"
        placeholder={filterConfig?.placeholder}
        {...register(`filters.${name}.search`)}
      />
      <CheckboxSection
        items={selectedFormItems}
        name={name}
        label={t(`books:filters.multiSelect.selected`)}
        selectAllDisabled={selectAllDisabled}
        onDeselectAll={
          showSelectAll && hasSelection && allSelected
            ? handleDeselectAll
            : undefined
        }
        onSelectAll={
          showSelectAll && hasSelection && !allSelected
            ? handleSelectAll
            : undefined
        }
      />
      {isLoadingSearch ? (
        <div className="flex w-full justify-center py-2 px-6 text-sm">
          <LoadingSpinner size="sm" />
        </div>
      ) : (
        <CheckboxSection
          items={suggestions}
          name={name}
          label={t(`books:filters.multiSelect.suggestions`)}
          tooltip={
            <InfoTooltip
              text={t("books:filters.multiSelect.suggestionsTooltip")}
              iconSize="small"
            />
          }
          noSuggestionsFound={
            !suggestions.length
              ? t("books:filters.multiSelect.noSuggestionsFound")
              : undefined
          }
          selectAllDisabled={selectAllDisabled}
          onSelectAll={
            showSelectAll && !hasSelection ? handleSelectAll : undefined
          }
        />
      )}
    </div>
  );
};
